import { TokenType } from './tokens'
import {
  type MinidownNode,
  type Root,
  MinidownNodeType,
  type Emphasis,
  type Strong,
  type Text,
} from './nodes'
import { tokenize } from './tokenize'

/**
 * Parses Minidown to an AST.
 */
export function parse(source: string): Root {
  const tokens = tokenize(source)

  let pos = 0
  const eos = tokens.length
  // Parse the root.
  return parseRoot()

  /**
   * Starts parsing at the root.
   */
  function parseRoot() {
    const root: Root = {
      type: MinidownNodeType.Root,
      children: [],
    }
    while (pos < eos) {
      const node = parseContent()
      if (node) {
        root.children.push(node)
      }
    }

    root.children = optimize(root.children)
    return root
  }

  /**
   * Parses content nodes.
   * Returns `null` if node would effectively be empty.
   */
  function parseContent(): MinidownNode | null {
    const startToken = tokens[pos]
    if (
      startToken.type === TokenType.StrongMarker ||
      startToken.type === TokenType.EmphasisMarker
    ) {
      const node: Strong | Emphasis = {
        type:
          startToken.type === TokenType.StrongMarker
            ? MinidownNodeType.Strong
            : MinidownNodeType.Emphasis,
        value: startToken.value,
        children: [],
      }
      pos++
      while (pos < eos) {
        const endToken = tokens[pos]
        if (
          endToken.type === startToken.type &&
          endToken.value === startToken.value
        ) {
          // We're done parsing this type of node.
          pos++
          if (node.children.length > 0) {
            node.children = optimize(node.children)
            return node
          }
          return null
        }

        const child = parseContent()
        if (child) {
          node.children.push(child)
        }
      }

      // If we reach this point, there is an unterminated strong or emphasis marker,
      // however that really shouldn't be possible as those are eliminated by the tokenizer.
      /* v8 ignore next */
      return null
    }

    return parseText()
  }

  /**
   * Parses text.
   */
  function parseText(): MinidownNode {
    const token = tokens[pos++]
    const node: Text = {
      type: MinidownNodeType.Text,
      value: token.value,
    }

    return node
  }
}

/**
 * Optimizes the nodes by consolidating consecutive text nodes.
 *
 * @param nodes
 */
function optimize(nodes: MinidownNode[]): MinidownNode[] {
  const consolidated: MinidownNode[] = []
  for (let i = 0; i < nodes.length; i++) {
    const next = nodes[i]
    if (i > 0 && next.type === MinidownNodeType.Text) {
      const prev = consolidated[consolidated.length - 1]
      if (prev.type === MinidownNodeType.Text) {
        prev.value += next.value
        continue
      }
    }
    consolidated.push(next)
  }
  return consolidated
}
